import { TBackendValidationErrorDetail } from "../../../../../services/api-integrations";
import { TWebhookMappingRow } from "../types";

export type TMappingValidationResult = {
  errors: Array<{ path: string; message: string }>;
  duplicateTargets: string[];
};

export const normalizeValidationLoc = (
  loc?: Array<string | number>,
): string => {
  if (!loc || !loc.length) return "payload";

  return loc
    .filter((segment) => segment !== "body")
    .map((segment) =>
      typeof segment === "number" ? `[${segment}]` : String(segment),
    )
    .reduce((acc, segment) => {
      if (!acc) return segment;
      if (segment.startsWith("[")) return `${acc}${segment}`;
      return `${acc}.${segment}`;
    }, "");
};

export const normalizeBackendValidationErrors = (
  errors: TBackendValidationErrorDetail[] | undefined,
): Array<{ path: string; message: string }> => {
  if (!errors?.length) return [];

  return errors.map((entry) => ({
    path: normalizeValidationLoc(entry.loc),
    message: entry.msg ?? "Invalid value",
  }));
};

export const validateMappingRules = (
  rows: TWebhookMappingRow[],
): TMappingValidationResult => {
  const errors: Array<{ path: string; message: string }> = [];
  const seenTargets = new Set<string>();
  const duplicateTargets = new Set<string>();

  rows.forEach((row, index) => {
    const targetField = row.target_field.trim();
    const sourcePath = row.source_path.trim();
    const literalValue = row.literal_value.trim();
    const defaultValue = row.default_value.trim();

    if (!targetField) {
      errors.push({
        path: `field_mappings[${index}].target_field`,
        message: "target_field is required.",
      });
    } else if (seenTargets.has(targetField)) {
      duplicateTargets.add(targetField);
    } else {
      seenTargets.add(targetField);
    }

    if (!sourcePath && !literalValue && !defaultValue) {
      errors.push({
        path: `field_mappings[${index}]`,
        message:
          "At least one of source_path, literal_value, or default_value is required.",
      });
    }
  });

  if (duplicateTargets.size) {
    errors.push({
      path: "field_mappings",
      message: `Duplicate target_field values: ${Array.from(duplicateTargets).join(", ")}`,
    });
  }

  return {
    errors,
    duplicateTargets: Array.from(duplicateTargets),
  };
};

const validateRequiredFields = (
  payload: unknown,
  schema: Record<string, unknown> | undefined,
): Array<{ path: string; message: string }> => {
  if (!schema || typeof schema !== "object") return [];

  const properties =
    typeof schema.properties === "object" && schema.properties
      ? (schema.properties as Record<string, unknown>)
      : {};
  const required = Array.isArray(schema.required) ? schema.required : [];

  return required
    .filter((field) => typeof field === "string")
    .flatMap((fieldName) => {
      const rootValue = payload as Record<string, unknown>;
      const fieldValue = rootValue?.[fieldName as string];
      if (fieldValue !== undefined && fieldValue !== null) {
        return [];
      }
      const descriptor = properties[fieldName as string] as
        | Record<string, unknown>
        | undefined;
      const title =
        typeof descriptor?.title === "string"
          ? descriptor.title
          : (fieldName as string);

      return [{ path: String(fieldName), message: `${title} is required.` }];
    });
};

export const validateSchemaCompatibility = (
  payload: unknown,
  createSchema: Record<string, unknown> | undefined,
): Array<{ path: string; message: string }> => {
  if (!payload || typeof payload !== "object") {
    return [{ path: "payload", message: "Payload must be an object." }];
  }

  return validateRequiredFields(payload, createSchema);
};
